import React from 'react';
import { Link } from 'react-router-dom';
import Navigation from '../../common/Navigation';
import Footer from '../../common/Footer';
import { motion } from 'framer-motion';
import { 
  DocumentTextIcon,
  ShieldCheckIcon,
  LockClosedIcon,
  UserGroupIcon,
  ServerIcon,
  GlobeAltIcon,
  ClockIcon,
  EnvelopeIcon
} from '@heroicons/react/24/outline';

const PrivacyPolicy: React.FC = () => {
  const tableOfContents = [
    { id: '1-introduction', title: 'Introduction', icon: DocumentTextIcon },
    { id: '2-interpretation-and-definitions', title: 'Interpretation and Definitions', icon: DocumentTextIcon },
    { id: '3-information-we-collect', title: 'Information We Collect', icon: ServerIcon },
    { id: '4-how-we-collect-your-information', title: 'How We Collect Your Information', icon: UserGroupIcon },
    { id: '5-use-of-your-personal-data', title: 'Use of Your Personal Data', icon: ShieldCheckIcon },
    { id: '6-disclosure-of-your-personal-data', title: 'Disclosure of Your Personal Data', icon: GlobeAltIcon },
    { id: '7-data-retention', title: 'Data Retention', icon: ClockIcon },
    { id: '8-transfer-of-your-personal-data', title: 'Transfer of Your Personal Data', icon: GlobeAltIcon },
    { id: '9-security-of-your-personal-data', title: 'Security of Your Personal Data', icon: LockClosedIcon },
    { id: '10-your-data-protection-rights', title: 'Your Data Protection Rights', icon: ShieldCheckIcon },
    { id: '11-childrens-privacy', title: "Children's Privacy", icon: UserGroupIcon },
    { id: '12-links-to-other-websites', title: 'Links to Other Websites', icon: GlobeAltIcon },
    { id: '13-changes-to-this-privacy-policy', title: 'Changes to This Privacy Policy', icon: DocumentTextIcon },
    { id: '14-contact-us', title: 'Contact Us', icon: EnvelopeIcon }
  ];

  const currentDate = new Date().toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gray-50">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-r from-primary-900 to-primary-700 py-16">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.h1 
              className="text-4xl md:text-5xl font-bold text-white mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              Privacy Policy
            </motion.h1>
            <motion.p 
              className="text-xl text-white/90"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              TWENTY FOUR CAPITAL LLC
            </motion.p>
            <motion.div 
              className="mt-4 text-white/80"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
            >
              <p>Effective Date: {currentDate}</p>
              <p>Last Updated: {currentDate}</p>
            </motion.div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            {/* Table of Contents Sidebar */}
            <aside className="lg:col-span-1">
              <div className="sticky top-32 bg-white rounded-lg shadow-sm p-6">
                <h2 className="text-lg font-semibold text-gray-900 mb-4">Table of Contents</h2>
                <nav className="space-y-2">
                  {tableOfContents.map((item, index) => (
                    <motion.a
                      key={item.id}
                      href={`#${item.id}`}
                      className="flex items-center gap-2 text-sm text-gray-600 hover:text-orange-500 transition-colors py-2"
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.05 }}
                      onClick={(e) => {
                        e.preventDefault();
                        const element = document.getElementById(item.id);
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth' });
                          window.history.pushState(null, '', `#${item.id}`);
                        }
                      }}
                    >
                      <item.icon className="h-4 w-4 text-gray-400" />
                      <span>{item.title}</span>
                    </motion.a>
                  ))}
                </nav>
              </div>
            </aside>

            {/* Main Content */}
            <main className="lg:col-span-3">
              <div className="bg-white rounded-lg shadow-sm p-8 space-y-12">
                {/* Section 1: Introduction */}
                <section id="1-introduction">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">1.</span> Introduction
                  </h2>
                  <div className="prose prose-gray max-w-none">
                    <p className="text-gray-700 leading-relaxed mb-4">
                      This Privacy Policy describes the policies and procedures of Twenty Four Capital LLC ("Company," "we," "us," or "our") on the collection, use, and disclosure of your information when you use our services ("Service") and tells you about your privacy rights and how the law protects you.
                    </p>
                    <p className="text-gray-700 leading-relaxed mb-4">
                      Twenty Four Capital LLC is a Wyoming Limited Liability Company operating as a family office providing tech-enabled equity finance solutions. We use your personal data to provide and improve our Service, including automated underwriting, transaction processing, data enrichment services, and proprietary analytics for equity-based financing solutions.
                    </p>
                    <p className="text-gray-700 leading-relaxed">
                      By using our Service, you agree to the collection and use of information in accordance with this Privacy Policy.
                    </p>
                  </div>
                </section>

                {/* Section 2: Interpretation and Definitions */}
                <section id="2-interpretation-and-definitions">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">2.</span> Interpretation and Definitions
                  </h2>
                  
                  <h3 className="text-xl font-semibold text-gray-800 mb-3">Interpretation</h3>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    Words with initial capital letters have meanings defined under the following conditions. These definitions shall have the same meaning regardless of whether they appear in singular or plural form.
                  </p>

                  <h3 className="text-xl font-semibold text-gray-800 mb-3">Definitions</h3>
                  <p className="text-gray-700 leading-relaxed mb-4">For the purposes of this Privacy Policy:</p>
                  
                  <div className="space-y-3">
                    {[
                      { term: "Account", def: "means a unique account created for you to access our Service or parts of our Service." },
                      { term: "Affiliate", def: "means an entity that controls, is controlled by, or is under common control with a party, where \"control\" means ownership of 50% or more of the shares, equity interest, or other securities entitled to vote for election of directors or other managing authority." },
                      { term: "API", def: "means Application Programming Interface, which allows different software applications to communicate with each other." },
                      { term: "Automated Underwriting System", def: "means our proprietary technology platform that uses algorithms and data analysis to evaluate financing applications." },
                      { term: "Company", def: "refers to Twenty Four Capital LLC, 1309 Coffeen Ave Ste 17816, Sheridan, WY 82801." },
                      { term: "Cookies", def: "are small files placed on your device containing details of your browsing history on our website among its many uses." },
                      { term: "Country", def: "refers to Wyoming, United States." },
                      { term: "Data Controller", def: "means the natural or legal person who (either alone or jointly with others) determines the purposes and means of processing Personal Data." },
                      { term: "Data Enrichment", def: "means the process of enhancing, refining, or otherwise improving raw data through third-party services and proprietary algorithms." },
                      { term: "Data Warehouse", def: "means our proprietary database systems storing metrics and analytics on publicly traded companies." },
                      { term: "Device", def: "means any device that can access the Service, such as a computer, cellphone, or digital tablet." },
                      { term: "Personal Data", def: "is any information that relates to an identified or identifiable individual." },
                      { term: "Service", def: "refers to the Website and all related technology platforms, APIs, and mobile applications." },
                      { term: "Service Provider", def: "means any natural or legal person who processes data on behalf of the Company." },
                      { term: "Third-party Social Media Service", def: "refers to any website or social network through which a user can log in or create an account to use the Service." },
                      { term: "Usage Data", def: "refers to data collected automatically, either generated by use of the Service or from the Service infrastructure itself." },
                      { term: "Website", def: "refers to Twenty Four Capital LLC's website, accessible from https://24lp.io." },
                      { term: "You", def: "means the individual accessing or using the Service, or the company or other legal entity on behalf of which such individual is accessing or using the Service." }
                    ].map((item, index) => (
                      <div key={index} className="flex">
                        <span className="font-semibold text-gray-800 mr-2">•</span>
                        <div>
                          <span className="font-semibold text-gray-800">{item.term}</span> {item.def}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Section 3: Information We Collect */}
                <section id="3-information-we-collect">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">3.</span> Information We Collect
                  </h2>

                  <div className="space-y-8">
                    {/* 3.1 Personal Data */}
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">3.1 Personal Data</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        While using our Service, we may ask you to provide us with certain personally identifiable information that can be used to contact or identify you. Personally identifiable information may include, but is not limited to:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>First name and last name</li>
                        <li>Email address</li>
                        <li>Phone number</li>
                        <li>Company name and position</li>
                        <li>Professional licenses and registrations (e.g., FINRA membership details)</li>
                        <li>Business address</li>
                        <li>Financial institution affiliations</li>
                        <li>Investment experience and qualifications</li>
                        <li>Accreditation status documentation</li>
                      </ul>
                    </div>

                    {/* 3.2 Company Information */}
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">3.2 Company Information</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        For publicly traded companies and their representatives, we collect:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Company name and ticker symbol</li>
                        <li>SEC filing information</li>
                        <li>Corporate structure details</li>
                        <li>Executive and director information</li>
                        <li>Financial statements and metrics</li>
                        <li>Trading history and volume data</li>
                        <li>Corporate actions and events</li>
                        <li>Industry classification codes</li>
                      </ul>
                    </div>

                    {/* 3.3 Transaction Data */}
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">3.3 Transaction Data</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        When processing equity financing transactions, we collect:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Transaction amount and terms</li>
                        <li>Financing structure details</li>
                        <li>Collateral information</li>
                        <li>Performance metrics</li>
                        <li>Payment history</li>
                        <li>Correspondence related to transactions</li>
                      </ul>
                    </div>

                    {/* 3.4 Usage Data */}
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">3.4 Usage Data</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        Usage Data is collected automatically when using the Service and may include:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Internet Protocol (IP) address</li>
                        <li>Browser type and version</li>
                        <li>Pages visited on our Service</li>
                        <li>Time and date of visits</li>
                        <li>Time spent on pages</li>
                        <li>Unique device identifiers</li>
                        <li>Diagnostic data</li>
                        <li>Click-through rates</li>
                        <li>Feature usage patterns</li>
                        <li>API call logs and frequencies</li>
                      </ul>
                    </div>

                    {/* 3.5 Tracking Technologies and Cookies */}
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">3.5 Tracking Technologies and Cookies</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        We use various tracking technologies to track activity on our Service:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li><strong>Cookies</strong>: Both session and persistent cookies</li>
                        <li><strong>Web Beacons</strong>: Electronic files to count users and verify system integrity</li>
                        <li><strong>JavaScript Tags</strong>: To analyze user behavior and improve functionality</li>
                        <li><strong>Local Storage</strong>: To maintain user preferences</li>
                        <li><strong>Analytics Tools</strong>: Including proprietary and third-party analytics</li>
                      </ul>
                    </div>

                    {/* 3.6 Data from Third-Party Sources */}
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">3.6 Data from Third-Party Sources</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        We collect and enrich data from various third-party sources:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>SEC EDGAR database</li>
                        <li>Financial data providers</li>
                        <li>Market data vendors</li>
                        <li>Credit bureaus (with appropriate permissions)</li>
                        <li>Public records databases</li>
                        <li>Industry research providers</li>
                        <li>Social media platforms (publicly available information)</li>
                        <li>Third-party data enrichment services</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Section 4: How We Collect Your Information */}
                <section id="4-how-we-collect-your-information">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">4.</span> How We Collect Your Information
                  </h2>

                  <div className="space-y-6">
                    {/* 4.1 Direct Collection */}
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">4.1 Direct Collection</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        We collect information directly from you when you:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Register for an account</li>
                        <li>Complete financing applications</li>
                        <li>Submit documents for verification</li>
                        <li>Contact us through our Service</li>
                        <li>Subscribe to our newsletters or alerts</li>
                        <li>Participate in surveys or assessments</li>
                      </ul>
                    </div>

                    {/* 4.2 Automated Collection */}
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">4.2 Automated Collection</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        We automatically collect information through:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Our proprietary algorithms analyzing public company data</li>
                        <li>API integrations with third-party services</li>
                        <li>Web scraping of publicly available information</li>
                        <li>Cookies and tracking technologies</li>
                        <li>Server logs and analytics tools</li>
                        <li>Mobile application usage</li>
                      </ul>
                    </div>

                    {/* 4.3 Third-Party Collection */}
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">4.3 Third-Party Collection</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        We receive information from third parties including:
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Data enrichment service providers</li>
                        <li>Marketing partners</li>
                        <li>Financial data aggregators</li>
                        <li>Professional verification services</li>
                        <li>Public databases and registries</li>
                      </ul>
                    </div>
                  </div>
                </section>

                {/* Section 5: Use of Your Personal Data */}
                <section id="5-use-of-your-personal-data">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">5.</span> Use of Your Personal Data
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    The Company uses Personal Data for the following purposes:
                  </p>

                  <div className="space-y-6">
                    {[
                      {
                        title: "5.1 Service Provision and Maintenance",
                        items: [
                          "To provide and maintain our Service",
                          "To process equity financing applications",
                          "To execute automated underwriting decisions",
                          "To manage transaction workflows",
                          "To service existing financing arrangements",
                          "To provide customer support"
                        ]
                      },
                      {
                        title: "5.2 Account Management",
                        items: [
                          "To manage your registration as a user",
                          "To verify professional qualifications and accreditation status",
                          "To provide access to Service features based on user type",
                          "To maintain security and prevent unauthorized access"
                        ]
                      },
                      {
                        title: "5.3 Business Operations",
                        items: [
                          "To develop and improve our proprietary algorithms",
                          "To enhance our underwriting formulas",
                          "To optimize transaction processing workflows",
                          "To analyze market trends and opportunities",
                          "To maintain our data warehouse of public company metrics",
                          "To conduct research and development"
                        ]
                      },
                      {
                        title: "5.4 Communication",
                        items: [
                          "To contact you regarding your account or transactions",
                          "To send transaction confirmations and updates",
                          "To provide market alerts and insights",
                          "To send newsletters and educational content (with consent)",
                          "To notify you of Service changes or updates"
                        ]
                      },
                      {
                        title: "5.5 Marketing and Business Development",
                        items: [
                          "To develop targeted product offerings",
                          "To analyze user behavior and preferences",
                          "To create personalized experiences",
                          "To identify potential business opportunities",
                          "To measure marketing campaign effectiveness",
                          "To conduct market research"
                        ]
                      },
                      {
                        title: "5.6 Compliance and Legal",
                        items: [
                          "To comply with legal obligations",
                          "To verify accredited investor status",
                          "To conduct anti-money laundering checks",
                          "To maintain transaction records",
                          "To respond to legal requests and prevent harm",
                          "To enforce our terms and conditions"
                        ]
                      },
                      {
                        title: "5.7 Analytics and Improvement",
                        items: [
                          "To analyze Service usage patterns",
                          "To identify system improvements",
                          "To develop new features and products",
                          "To optimize user experience",
                          "To benchmark performance metrics",
                          "To train and improve our AI models"
                        ]
                      }
                    ].map((section, index) => (
                      <div key={index}>
                        <h3 className="text-xl font-semibold text-gray-800 mb-3">{section.title}</h3>
                        <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                          {section.items.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    ))}
                  </div>
                </section>

                {/* Section 6: Disclosure of Your Personal Data */}
                <section id="6-disclosure-of-your-personal-data">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">6.</span> Disclosure of Your Personal Data
                  </h2>

                  <div className="space-y-6">
                    {[
                      {
                        title: "6.1 Service Providers",
                        content: "We may share your information with third-party service providers:",
                        items: [
                          "Cloud infrastructure providers",
                          "Data enrichment services",
                          "Analytics providers",
                          "Payment processors",
                          "Communication platforms",
                          "Professional verification services",
                          "Marketing automation providers",
                          "Customer relationship management systems"
                        ]
                      },
                      {
                        title: "6.2 Business Partners",
                        content: "With your consent, we may share information with:",
                        items: [
                          "FINRA member firms",
                          "Other accredited investors",
                          "Professional advisors",
                          "Industry partners",
                          "Co-investors in transactions"
                        ]
                      },
                      {
                        title: "6.3 Business Transfers",
                        content: "In the event of a merger, acquisition, or asset sale, your Personal Data may be transferred. We will provide notice before your Personal Data becomes subject to a different Privacy Policy."
                      },
                      {
                        title: "6.4 Legal Requirements",
                        content: "We may disclose your Personal Data when required to:",
                        items: [
                          "Comply with legal obligations",
                          "Respond to valid legal requests",
                          "Protect our rights or property",
                          "Prevent wrongdoing",
                          "Protect personal safety",
                          "Defend against legal liability"
                        ]
                      },
                      {
                        title: "6.5 Aggregated Data",
                        content: "We may share aggregated, anonymized data that cannot identify you:",
                        items: [
                          "Market research reports",
                          "Industry benchmarks",
                          "Transaction volume statistics",
                          "Performance metrics",
                          "Trend analyses"
                        ]
                      }
                    ].map((section, index) => (
                      <div key={index}>
                        <h3 className="text-xl font-semibold text-gray-800 mb-3">{section.title}</h3>
                        {section.content && (
                          <p className="text-gray-700 leading-relaxed mb-3">{section.content}</p>
                        )}
                        {section.items && (
                          <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                            {section.items.map((item, idx) => (
                              <li key={idx}>{item}</li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}
                  </div>
                </section>

                {/* Section 7: Data Retention */}
                <section id="7-data-retention">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">7.</span> Data Retention
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    We retain your Personal Data only as long as necessary for the purposes set out in this Privacy Policy. Retention periods vary based on:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4 mb-6">
                    <li>Type of data collected</li>
                    <li>Purpose of processing</li>
                    <li>Legal requirements</li>
                    <li>Business needs</li>
                  </ul>
                  <p className="text-gray-700 leading-relaxed mb-3">Typical retention periods:</p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                    <li>Account information: Duration of relationship plus 7 years</li>
                    <li>Transaction records: 7 years from completion</li>
                    <li>Marketing data: Until opt-out or 3 years of inactivity</li>
                    <li>Usage data: 2 years</li>
                    <li>Cookies: As specified in our Cookie Policy</li>
                  </ul>
                </section>

                {/* Section 8: Transfer of Your Personal Data */}
                <section id="8-transfer-of-your-personal-data">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">8.</span> Transfer of Your Personal Data
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    Your information may be transferred to and maintained on servers located outside your jurisdiction. We ensure appropriate safeguards are in place for international transfers, including:
                  </p>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                    <li>Standard contractual clauses</li>
                    <li>Privacy Shield certification (where applicable)</li>
                    <li>Adequacy decisions</li>
                    <li>Appropriate technical and organizational measures</li>
                  </ul>
                </section>

                {/* Section 9: Security of Your Personal Data */}
                <section id="9-security-of-your-personal-data">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">9.</span> Security of Your Personal Data
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    We implement appropriate technical and organizational security measures:
                  </p>

                  <div className="space-y-6">
                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">9.1 Technical Measures</h3>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Encryption of data in transit and at rest</li>
                        <li>Secure socket layer (SSL) technology</li>
                        <li>Firewalls and intrusion detection systems</li>
                        <li>Regular security audits and penetration testing</li>
                        <li>Access controls and authentication protocols</li>
                        <li>Secure API endpoints</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">9.2 Organizational Measures</h3>
                      <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                        <li>Limited access on a need-to-know basis</li>
                        <li>Confidentiality agreements with employees</li>
                        <li>Regular security training</li>
                        <li>Incident response procedures</li>
                        <li>Vendor security assessments</li>
                        <li>Data protection impact assessments</li>
                      </ul>
                    </div>

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">9.3 Limitations</h3>
                      <p className="text-gray-700 leading-relaxed">
                        While we strive to protect your Personal Data, no method of transmission over the Internet or electronic storage is 100% secure. We cannot guarantee absolute security.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Section 10: Your Data Protection Rights */}
                <section id="10-your-data-protection-rights">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">10.</span> Your Data Protection Rights
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-6">
                    Depending on your location, you may have the following rights:
                  </p>

                  <div className="space-y-6">
                    {[
                      {
                        title: "10.1 Access Rights",
                        items: [
                          "Request access to your Personal Data",
                          "Receive a copy of your data in a portable format",
                          "Know what data we hold about you"
                        ]
                      },
                      {
                        title: "10.2 Correction Rights",
                        items: [
                          "Request correction of inaccurate data",
                          "Complete incomplete data",
                          "Update outdated information"
                        ]
                      },
                      {
                        title: "10.3 Deletion Rights",
                        items: [
                          "Request deletion of your Personal Data",
                          "Subject to legal retention requirements",
                          "May impact Service availability"
                        ]
                      },
                      {
                        title: "10.4 Processing Rights",
                        items: [
                          "Object to certain processing activities",
                          "Restrict processing in specific circumstances",
                          "Withdraw consent where processing is based on consent"
                        ]
                      },
                      {
                        title: "10.5 Opt-Out Rights",
                        items: [
                          "Opt out of marketing communications",
                          "Disable cookies through browser settings",
                          "Opt out of data sales (where applicable)"
                        ]
                      }
                    ].map((section, index) => (
                      <div key={index}>
                        <h3 className="text-xl font-semibold text-gray-800 mb-3">{section.title}</h3>
                        <ul className="list-disc list-inside space-y-1 text-gray-700 ml-4">
                          {section.items.map((item, idx) => (
                            <li key={idx}>{item}</li>
                          ))}
                        </ul>
                      </div>
                    ))}

                    <div>
                      <h3 className="text-xl font-semibold text-gray-800 mb-3">10.6 Exercising Your Rights</h3>
                      <p className="text-gray-700 leading-relaxed mb-3">
                        To exercise any of these rights, contact us at:
                      </p>
                      <div className="bg-gray-50 rounded-lg p-4 text-gray-700">
                        <p className="mb-1">Email: privacy@24lp.io</p>
                        <p>Mail: 1309 Coffeen Ave Ste 17816, Sheridan, WY 82801</p>
                      </div>
                      <p className="text-gray-700 leading-relaxed mt-3">
                        We will respond to your request within 30 days.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Section 11: Children's Privacy */}
                <section id="11-childrens-privacy">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">11.</span> Children's Privacy
                  </h2>
                  <p className="text-gray-700 leading-relaxed">
                    Our Service is intended for sophisticated capital market professionals. We do not knowingly collect data from individuals under 18 years of age. If you become aware that a child has provided us with Personal Data, please contact us immediately.
                  </p>
                </section>

                {/* Section 12: Links to Other Websites */}
                <section id="12-links-to-other-websites">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">12.</span> Links to Other Websites
                  </h2>
                  <p className="text-gray-700 leading-relaxed">
                    Our Service may contain links to third-party websites. We are not responsible for the privacy practices of these external sites. We encourage you to review their privacy policies.
                  </p>
                </section>

                {/* Section 13: Changes to This Privacy Policy */}
                <section id="13-changes-to-this-privacy-policy">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">13.</span> Changes to This Privacy Policy
                  </h2>
                  <p className="text-gray-700 leading-relaxed">
                    We may update our Privacy Policy periodically. Changes will be posted on this page with an updated "Last Updated" date. For material changes, we will notify you via email or prominent notice on our Service.
                  </p>
                </section>

                {/* Section 14: Contact Us */}
                <section id="14-contact-us">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    <span className="text-orange-500">14.</span> Contact Us
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    If you have questions about this Privacy Policy, contact us:
                  </p>
                  
                  <div className="bg-orange-50 rounded-lg p-6 mb-6">
                    <h3 className="font-semibold text-gray-900 mb-3">Twenty Four Capital LLC</h3>
                    <p className="text-gray-700 mb-1">1309 Coffeen Ave Ste 17816</p>
                    <p className="text-gray-700 mb-3">Sheridan, WY 82801</p>
                    <p className="text-gray-700 mb-1">Email: privacy@24lp.io</p>
                    <p className="text-gray-700">Website: https://24lp.io</p>
                  </div>

                  <p className="text-gray-700 leading-relaxed">
                    For privacy-related complaints that cannot be resolved directly with us, you may contact the appropriate data protection authority in your jurisdiction.
                  </p>
                </section>
              </div>
            </main>
          </div>
        </div>

        {/* Footer */}
        <Footer />
      </div>
    </>
  );
};

export default PrivacyPolicy; 